import { ProductItem } from 'lane-shared/domains/billingPayments/types';
import { type SupportedPaymentCurrency } from 'domains-billing-currency';
import { SupportedLocaleEnum } from 'localization';
import { productToPriceComponents } from 'lane-shared/domains/billingPayments/helpers';
import { getPricing } from 'lane-shared/domains/productCatalog/helpers';

/**
 * Create an updated product item with recalculated pricing
 * after a rate override
 */
export const createUpdatedProductItem = (
  item: ProductItem,
  overrideAmount: number,
  currency: SupportedPaymentCurrency,
  locale: SupportedLocaleEnum,
  userInputRate?: number,
  isAdminFeeFeatureEnabled = false
): ProductItem => {
  const roundToTwoDecimals = (value: number): number => {
    return Math.round(value * 100) / 100;
  };

  let originalPrice = item.product.originalPrice;

  if (userInputRate !== undefined && !item.isRateOverridden) {
    if (!originalPrice || item.product.sellingPrice?.listPrice !== undefined) {
      originalPrice = {
        amount: item.product.amount,
        markup: item.product.markup || undefined,
        tax: item.product.tax || undefined,
      };
    }
  }

  const updatedProduct = {
    ...item.product,
    amount: overrideAmount,
    sellingPrice: undefined,
    originalPrice,
  };

  const priceComponents = productToPriceComponents(updatedProduct);

  const { grossMarkup, grossTax, listPrice, netPrice, grossAdminFee, grossAdminFeeTax } = getPricing({
    currency,
    locale,
    priceComponents,
    isAdminFeeFeatureEnabled,
  });

  const roundedPricing = {
    grossTax: roundToTwoDecimals(grossTax),
    grossMarkup: roundToTwoDecimals(grossMarkup),
    listPrice: roundToTwoDecimals(listPrice),
    netPrice: roundToTwoDecimals(netPrice),
    grossAdminFee: roundToTwoDecimals(grossAdminFee),
    grossAdminFeeTax: roundToTwoDecimals(grossAdminFeeTax),
  };

  return {
    ...item,
    product: {
      ...updatedProduct,
      amount: roundToTwoDecimals(overrideAmount),
      sellingPrice: roundedPricing,
    },
    isRateOverridden: true,
    userInputRate: userInputRate ?? roundedPricing.listPrice,
  };
};
